import { FC, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, CircularProgress, Stack, Typography, Skeleton, Divider, Paper } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import jwt_decode from 'jwt-decode';
import { fetchInvoiceById, fetchInvoicePreview, fetchInvoices } from '../../store/invoiceStore/invoiceThunk';
import { InvoicePreviewPayload } from "../../utils/invoice.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Utils, VocuherCategory } from "../../utils/common-utils";
import InvoiceNavigationBar from '../../components/Invoice/InvoiceNavigationBar';
import { toast } from 'react-toastify';
import { Button } from '@mui/material';

// Enhanced Preview Invoice page with navigation filters
const InvoicePreviewWithFilters: FC = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const voucherType = searchParams.get("type") || '';

  const dispatch = useAppDispatch();
  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);
  const { invoices } = useAppSelector(state => state.invoices);

  const [pdfLocation, setPdfLocation] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefersh, setIsRefersh] = useState("");
  const [currentInvoiceId, setCurrentInvoiceId] = useState(invoiceId);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 20;



  // Update currentInvoiceId when URL params change
  useEffect(() => {
    if (invoiceId && invoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(invoiceId);
      setIsLoading(true);
    }
  }, [invoiceId]);

  // Fetch invoices in batches for navigation, then fetch current invoice details
  const fetchInvoiceBatch = async (page: number = 1, append: boolean = false) => {
    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn && voucherType) {
      const type = voucherType === VocuherCategory.SALE ? VocuherCategory.SALE : VocuherCategory.PURCHASE;
      const skip = (page - 1) * pageSize;
      
      if (append) setLoadingMore(true);
      
      try {
        const result: any = await dispatch(fetchInvoices({
          accountId,
          type,
          params: `&limit=${pageSize}&skip=${skip}`
        }));
        
        const newInvoices = result.payload?.invoices || [];
        setHasMoreData(newInvoices.length === pageSize);
        
        console.log(`Fetched ${newInvoices.length} invoices for page ${page}`);
        
        // After first batch or when current invoice changes, fetch invoice details
        if ((page === 1 || !append) && currentInvoiceId && isLoggedIn) {
          dispatch(fetchInvoiceById(currentInvoiceId));
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setHasMoreData(false);
      } finally {
        if (append) setLoadingMore(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchInvoiceBatch(1, false);
  }, [selectedAccount._id, isLoggedIn, voucherType]);
  
  // Load current invoice details when ID changes
  useEffect(() => {
    if (currentInvoiceId && isLoggedIn) {
      dispatch(fetchInvoiceById(currentInvoiceId));
    }
  }, [currentInvoiceId, dispatch, isLoggedIn]);

  useEffect(() => {
    const getPreview = async () => {
      console.log('Fetching preview for invoice:', currentInvoiceId, 'with template:', templateId);
      let reqObj: InvoicePreviewPayload = {
        invoiceId: currentInvoiceId,
        templateId: templateId
      };
      let invoicePreviewResponse: any = await dispatch(fetchInvoicePreview(reqObj));
      console.log("===>", invoicePreviewResponse);
      let location = null;
      if (invoicePreviewResponse?.payload) {
        if (invoicePreviewResponse.payload.length > 0) {
          location = invoicePreviewResponse.payload[0].Location;
        } else {
          location = invoicePreviewResponse.payload.Location;
        }
        console.log('PDF location updated to:', location);
        setPdfLocation(location);
      }
      setIsLoading(false);
    };

    if (templateId && currentInvoiceId) {
      getPreview();
    } else if (currentInvoiceId) {
      try {
        let token: any = jwt_decode(currentInvoiceId);
        setPdfLocation(token.pdfLcation);
        setIsLoading(false);
      } catch (error) {
        console.log("Failed! Invalid Token", error);
        setIsLoading(false);
      }
    }
  }, [currentInvoiceId, isRefersh, templateId, dispatch]);

  const actionChangeHandler = (data: any) => {
    setIsRefersh(data.isRefersh);
  }

  const handleInvoiceChange = (newInvoiceId: string, newTemplateId: string, vCategory: string) => {
    if (newInvoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(newInvoiceId);
      setIsLoading(true);
      setPdfLocation(''); // Clear old PDF
      
      // Fetch invoice details when changing invoice
      if (isLoggedIn) {
        dispatch(fetchInvoiceById(newInvoiceId));
      }
      
      // Update URL with new invoice ID
      console.log("vCategory from handleInvoiceChange 111", vCategory)
      const newVoucherType = vCategory === VocuherCategory.SALE ? VocuherCategory.SALE : VocuherCategory.PURCHASE;
      navigate(`/invoice/${newInvoiceId}/preview?templateId=${newTemplateId || templateId}&type=${newVoucherType}`, {
        replace: true
      });
    }
  };
  
  // Handle loading more data when needed
  const handleLoadMore = () => {
    if (hasMoreData && !loadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchInvoiceBatch(nextPage, true);
    }
  };
  
  // Handle direct invoice search - load all matching invoices
  const handleDirectInvoiceSearch = async (invoiceNumber: string): Promise<boolean> => {
    if (!invoiceNumber.trim()) return false;
    
    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      try {
        // Don't set loading for the entire page, just for search
        
        // Search for all invoices matching the number pattern (no limit)
        const result: any = await dispatch(fetchInvoices({
          accountId,
          type: voucherType === 'sale' || voucherType === 'sales' ? VocuherCategory.SALE : VocuherCategory.PURCHASE,
          params: `&invoiceNumber=${encodeURIComponent(invoiceNumber)}` // Remove limit to get all matching
        }));
        
        const foundInvoices = result.payload?.invoices || [];
        
        if (foundInvoices.length > 0) {
          // Replace current invoice list with search results
          // This will update the navigation to only show matching invoices
          console.log(`Found ${foundInvoices.length} invoices matching "${invoiceNumber}"`);
          
          // Load the first matching invoice
          const firstInvoice = foundInvoices[0];
          
          // Fetch the full invoice details for the first invoice
          await dispatch(fetchInvoiceById(firstInvoice._id));
          
          // Navigate to the first invoice
          handleInvoiceChange(
            firstInvoice._id, 
            firstInvoice.pdfTemplate || templateId || '', 
            firstInvoice.voucherCategory || voucherType || VocuherCategory.SALE
          );
          
          // Return true to indicate search was successful
          return true;
        } else {
          // Don't break the UI, just show a message
          alert(`No invoices found matching "${invoiceNumber}". Please try a different search.`);
          // Keep the existing invoice list intact
          return false;
        }
      } catch (error) {
        console.error('Error searching invoice:', error);
        alert('Error searching for invoice. Please try again.');
        return false;
      }
    }
    return false;
  };

  return (
    <>
      <Box sx={{
        backgroundColor: '#fafafb',
        minHeight: '100vh',
        pt: 0,
        pb: 2 }}>
        {/* Top Section with Navigation Bar, Party Details and Quick Stats */}
        {isLoggedIn && (
          <Box sx={{ p: { xs: 1, sm: 2 }, pb: 1 }}>
            <Grid container spacing={2}>
              {/* Navigation Bar and Actions - Left Side */}
              
              <Grid item xs={12} md={12}>
                {(invoices && invoices.length > 0 && currentInvoiceId) && (
                  <InvoiceNavigationBar
                    currentInvoiceId={currentInvoiceId || ''}
                    voucherCategory={invoiceDetails?.voucherCategory || ''}
                    voucherType={voucherType}
                    onInvoiceChange={handleInvoiceChange}
                    invoices={invoices}
                    currentInvoiceDetails={invoiceDetails}
                    onActionChange={actionChangeHandler}
                    onLoadMore={handleLoadMore}
                    hasMoreData={hasMoreData}
                    loadingMore={loadingMore}
                    onDirectSearch={handleDirectInvoiceSearch}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Grid container spacing={2} sx={{
          p: { xs: 1, sm: 2 }
        }}>
          <Grid item xs={12} md={8} sx={{
            order: { xs: 2, md: 1 },
            mt: { xs: 2, md: 0 }
          }}>
            {(isLoading ?
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                p: 3,
                height: { xs: '60vh', md: '85vh' }
              }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Loading Preview ...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
              :
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                height: { xs: '60vh', md: '85vh' }
              }}>
                <object
                  style={{ height: "100%", width: "100%" }}
                  data={pdfLocation}
                  type="application/pdf"
                />
              </Box>
            )}
          </Grid>

          {/* Right side - Header and Party Details */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>

            {/* Quick Stats - Right Side */}
            <Grid item xs={12} md={12} sx={{pb: 2}}>
              {invoices && invoices.length > 0 && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5,
                    m: 0,
                    border: '1px solid #e9ecf1',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    {/* Total Invoices */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      borderRight: '1px solid #e9ecf1',
                      pr: 1
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'text.primary',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {invoices.length}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Total
                      </Typography>
                    </Box>

                    {/* Pending Invoices */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      borderRight: '1px solid #e9ecf1',
                      px: 1
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'warning.main',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {invoices.filter(inv => inv.status !== 'PAID').length}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Pending
                      </Typography>
                    </Box>

                    {/* Paid Invoices */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      pl: 1
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'success.main',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {invoices.filter(inv => inv.status === 'PAID').length}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Paid
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Grid>


            {/* Enhanced Party Details Section */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #e9ecf1',
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{
                bgcolor: '#f5f5f5',
                p: { xs: 1.5, sm: 2 },
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                      }}
                    >
                      {invoiceDetails?.voucherType} Preview
                    </Typography>
                <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                    >
                      {invoiceDetails?.date
                        ? new Date(invoiceDetails.date).toLocaleDateString('en-IN')
                        : 'N/A'} • #{invoiceDetails?.invoiceNumber} • {invoiceDetails?.status}
                    </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={{ xs: 1.5, sm: 2 }}>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Party Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceDetails?.buyer?.name || invoiceDetails?.supplier?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Amount
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}
                    >
                      {/* { Utils.formatNumber(parseFloat(invoiceDetails?.amount + Utils.additionalChargesSum(invoiceDetails)).toFixed(2) || '0') } */}

                      { Utils.formatNumber(parseFloat(invoiceDetails?.finalAmount || (invoiceDetails?.amount + Utils.additionalChargesSum(invoiceDetails))).toFixed(2) || '0') }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Date
                    </Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.date
                        ? new Date(invoiceDetails.date).toLocaleDateString('en-IN')
                        : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: invoiceDetails?.status === 'PAID'
                          ? 'success.main'
                          : 'warning.main'
                      }}
                    >
                      {invoiceDetails?.status}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Type
                    </Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.voucherType || 'N/A'}
                    </Typography>
                  </Box>
                  
                </Stack>
              </Box>
            </Box>

          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default InvoicePreviewWithFilters;